import React, {FunctionComponent, PropsWithChildren, useCallback, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Col} from "@pg-design/grid-module";
import {flex, mv, pb, prettyScroll} from "@pg-design/helpers-css";
import {IInfiniteScrollItemProps, InfiniteScrollList, useInfiniteList} from "@pg-design/infinite-list";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs";
import {RequestState} from "@pg-mono/request-state";

import {ColumnList} from "../../../atoms/ColumnList";
import {OfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {showOfferModal} from "../../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {IOfferModalName} from "../../../offer/types/OfferModalName";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IVendorOffer} from "../../../offer/types/VendorOffer";
import {Country} from "../../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetVendorDetailOffersQuery} from "../../api/get_vendor_detail_offers";
import {resetActiveOffersPage, setActiveOffersNextPage, setVendorActiveRegion} from "../../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../../utils/convert_to_vendor_detail_view_offers_payload";
import {VendorDetailOfferModal} from "./VendorDetailOfferModal";

interface IProps {
    vendor: {
        id: number;
        stats: {
            offers_count_for_sale: number;
            properties_count_for_sale: number;
        };
    };
    cities: {
        id: number;
        name: string;
    }[];
    openMapBtnClick: () => void;
    offersInRegionRequest: RequestState;
}

const modalName: IOfferModalName = "mainOfferModal";

export const VendorDetailActiveOffers: FunctionComponent<IProps> = React.memo((props) => {
    const {offersInRegionRequest, cities, vendor} = props;
    const dispatch = useAppDispatch();
    const [isIntersecting, setIsIntersecting] = useState(false);
    const intersectionId = "vendor-detail-active-offer-list";

    //  City change
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const changeRegion = useCallback(
        (regionId: number) => {
            // when `regionId` is the same we do NOT toggle button
            if (regionId === selectedCity) {
                return;
            }

            const regionIdNormalized = regionId === -1 ? null : regionId;
            // fetch proper list
            dispatch(setVendorActiveRegion(regionIdNormalized));
            dispatch(resetActiveOffersPage());
            setSelectedCity(regionIdNormalized);
        },
        [props.vendor.id, selectedCity]
    );

    const page = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.page);
    const {currentData: queryData, isUninitialized} = useGetVendorDetailOffersQuery({
        ...convertToVendorDetailViewOffersPayload({vendorId: vendor.id, region: selectedCity, displayType: OfferDisplayType.FOR_SALE}),
        page
    });

    const infiniteList = useInfiniteList(
        queryData,
        () => {
            dispatch(setActiveOffersNextPage());
        },
        isIntersecting,
        isUninitialized
    );

    const modalItems: IOfferModalOffer[] = infiniteList.items.map((item) => item.data);

    const itemComponent = useCallback(
        (props: IInfiniteScrollItemProps<IVendorOffer>) => {
            const {item, index} = props;

            return (
                <Col key={item.id} sm={4} md={4} as="li" css={[flex("normal", "center")]}>
                    <OfferBox
                        offer={item}
                        onShowOfferDetailsBtnClick={() => {
                            dispatch(showOfferModal({modalName, offerId: item.id, offer: item}));
                        }}
                        unlinkVendor
                        showFullPrice={item.region.country === Country.SPAIN}
                        index={index}
                        gtmMeta={{
                            listName: "Aktywne oferty dewelopera",
                            listId: "aktywne_oferty_dewelopera",
                            source: "additional",
                            tileEvents: {
                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                            }
                        }}
                    />
                </Col>
            );
        },
        [dispatch]
    );

    return (
        <>
            <div css={filterTabsHolder}>
                {cities.length > 1 && (
                    <Tabs<number>
                        color="gray"
                        variant="contained"
                        fullWidth
                        onChange={changeRegion}
                        value={selectedCity ?? -1}
                        tabs={[
                            {
                                title: "Wszystkie",
                                value: -1
                            },
                            ...cities.map((region) => ({
                                title: region.name,
                                value: region.id
                            }))
                        ]}
                    />
                )}
            </div>

            <ColumnList fluid css={offerListWrapper}>
                <InfiniteScrollList
                    listComponent={OfferListWrapper}
                    itemComponent={itemComponent}
                    infiniteList={infiniteList}
                    listIntersectionContainerId={intersectionId}
                    handleIsIntersecting={(isIntersecting) => {
                        setIsIntersecting(isIntersecting);
                    }}
                />
            </ColumnList>

            {offersInRegionRequest === RequestState.Waiting && (
                <div css={loaderHolder}>
                    <Loader size="md" />
                </div>
            )}

            <VendorDetailOfferModal vendorId={props.vendor.id} region={selectedCity} items={modalItems} />
        </>
    );
});

function OfferListWrapper(props: PropsWithChildren) {
    const {children} = props;
    return <>{children}</>;
}

const loaderHolder = css`
    display: flex;
    justify-content: center;
`;

const filterTabsHolder = (theme: Theme) => css`
    overflow-x: auto;
    overflow-y: hidden;
    ${pb(1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${prettyScroll()};
    }
`;

const offerListWrapper = (theme: Theme) => css`
    ${mv(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mv(4)};
    }
`;
import React, {FunctionComponent, PropsWithChildren, useCallback, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Col} from "@pg-design/grid-module";
import {flex, mv, pb, prettyScroll} from "@pg-design/helpers-css";
import {IInfiniteScrollItemProps, InfiniteScrollList, useInfiniteList} from "@pg-design/infinite-list";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs";
import {RequestState} from "@pg-mono/request-state";

import {ColumnList} from "../../../atoms/ColumnList";
import {OfferBox} from "../../../offer/components/offer_box/OfferBox";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {showOfferModal} from "../../../offer/state/offer_modals_slice";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {IOfferModalName} from "../../../offer/types/OfferModalName";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IVendorOffer} from "../../../offer/types/VendorOffer";
import {Country} from "../../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetVendorDetailOffersQuery} from "../../api/get_vendor_detail_offers";
import {resetActiveOffersPage, setActiveOffersNextPage, setVendorActiveRegion} from "../../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../../utils/convert_to_vendor_detail_view_offers_payload";
import {VendorDetailOfferModal} from "./VendorDetailOfferModal";

interface IProps {
    vendor: {
        id: number;
        stats: {
            offers_count_for_sale: number;
            properties_count_for_sale: number;
        };
    };
    cities: {
        id: number;
        name: string;
    }[];
    openMapBtnClick: () => void;
    offersInRegionRequest: RequestState;
}

const modalName: IOfferModalName = "mainOfferModal";

export const VendorDetailActiveOffers: FunctionComponent<IProps> = React.memo((props) => {
    const {offersInRegionRequest, cities, vendor} = props;
    const dispatch = useAppDispatch();
    const [isIntersecting, setIsIntersecting] = useState(false);
    const intersectionId = "vendor-detail-active-offer-list";

    //  City change
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const changeRegion = useCallback(
        (regionId: number) => {
            // when `regionId` is the same we do NOT toggle button
            if (regionId === selectedCity) {
                return;
            }

            const regionIdNormalized = regionId === -1 ? null : regionId;
            // fetch proper list
            dispatch(setVendorActiveRegion(regionIdNormalized));
            dispatch(resetActiveOffersPage());
            setSelectedCity(regionIdNormalized);
        },
        [props.vendor.id, selectedCity]
    );

    const page = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.page);
    const {currentData: queryData, isUninitialized} = useGetVendorDetailOffersQuery({
        ...convertToVendorDetailViewOffersPayload({vendorId: vendor.id, region: selectedCity, displayType: OfferDisplayType.FOR_SALE}),
        page
    });

    const infiniteList = useInfiniteList(
        queryData,
        () => {
            dispatch(setActiveOffersNextPage());
        },
        isIntersecting,
        isUninitialized
    );

    const modalItems: IOfferModalOffer[] = infiniteList.items.map((item) => item.data);

    const itemComponent = useCallback(
        (props: IInfiniteScrollItemProps<IVendorOffer>) => {
            const {item, index} = props;

            return (
                <Col key={item.id} sm={4} md={4} as="li" css={[flex("normal", "center")]}>
                    <OfferBox
                        offer={item}
                        onShowOfferDetailsBtnClick={() => {
                            dispatch(showOfferModal({modalName, offerId: item.id, offer: item}));
                        }}
                        unlinkVendor
                        showFullPrice={item.region.country === Country.SPAIN}
                        index={index}
                        gtmMeta={{
                            listName: "Aktywne oferty dewelopera",
                            listId: "aktywne_oferty_dewelopera",
                            source: "additional",
                            tileEvents: {
                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                            }
                        }}
                    />
                </Col>
            );
        },
        [dispatch]
    );

    return (
        <>
            <div css={filterTabsHolder}>
                {cities.length > 1 && (
                    <Tabs<number>
                        color="gray"
                        variant="contained"
                        fullWidth
                        onChange={changeRegion}
                        value={selectedCity ?? -1}
                        tabs={[
                            {
                                title: "Wszystkie",
                                value: -1
                            },
                            ...cities.map((region) => ({
                                title: region.name,
                                value: region.id
                            }))
                        ]}
                    />
                )}
            </div>

            <ColumnList fluid css={offerListWrapper}>
                <InfiniteScrollList
                    listComponent={OfferListWrapper}
                    itemComponent={itemComponent}
                    infiniteList={infiniteList}
                    listIntersectionContainerId={intersectionId}
                    handleIsIntersecting={(isIntersecting) => {
                        setIsIntersecting(isIntersecting);
                    }}
                />
            </ColumnList>

            {offersInRegionRequest === RequestState.Waiting && (
                <div css={loaderHolder}>
                    <Loader size="md" />
                </div>
            )}

            <VendorDetailOfferModal vendorId={props.vendor.id} region={selectedCity} items={modalItems} />
        </>
    );
});

function OfferListWrapper(props: PropsWithChildren) {
    const {children} = props;
    return <>{children}</>;
}

const loaderHolder = css`
    display: flex;
    justify-content: center;
`;

const filterTabsHolder = (theme: Theme) => css`
    overflow-x: auto;
    overflow-y: hidden;
    ${pb(1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${prettyScroll()};
    }
`;

const offerListWrapper = (theme: Theme) => css`
    ${mv(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mv(4)};
    }
`;
